import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Box, TextField, Typography } from "@mui/material";
import SimpleDataRender from "../../components/SimpleDataRender/SimpleDataRender";
import ProfileImgForm from "../../components/ProfileImgForm/ProfileImgForm";
import BackdropLoaderService from "../../components/BackdropLoader/backdropLoaderService";
import HeaderImgForm from "../../components/HeaderImgForm/HeaderImgForm";
import DialogService from "../../components/Dialog/dialogService";
import NotificationService from "../../components/Notification/notificationService";
import PremiumWrapperButton from "../../components/PremiumWrapperButton/PremiumWrapperButton";
import CustomSwitch from "../../components/CustomSwitch/CustomSwitch";
import { RootState } from "../../state";
import { Image } from "../../services/shirts/types";
import FormatService from "../../services/formatService";
import UserService from "../../services/userService";
import LoginService from "../../services/loginService";
import { Collection, UserData } from "../../types/authTypes";
import { Actions, Subjects } from "../../permission/constants";
import TextFieldUpdater from "../../components/TextfieldUpdater/TextFieldUpdater_v2";

enum LABELS {
  TITLE = "My account",
  USER_NAME = "Username",
  EMAIL_ADDRESS = "Email address",
  PASSWORD = "Password",
  PROFILE_PICTURE = "Picture/Image",
  HEADER_PICTURE = "Profile cover image",
  ABOUT_ME = "About me and my collection",
  VISIBILITY_COLLECTION = "Visibility of my collection",
  VISIBILITY_FIN_STATS = "Visibility of my financial stats",
  VISIBILITY_NUM_STATS = "Visibility of my numerical stats",
  BTN_EMAIL = "Change email address",
  BTN_ABOUT_ME = "Change About Me",
  BTN_PASSWORD = "Change password",
  BTN_IMAGE = "Upload an image",
}

const tfMainStyle = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "0px",
  },
};

const tfLabelProps = {
  sx: {
    color: "#6f7d95",
    fontSize: "1rem !important",
    fontWeight: 500,
  },
};

const tfInputProps = {
  disableUnderline: true,
  sx: {
    backgroundColor: "#f4f6f9",
    borderRadius: "3px",
    color: "#6f7d95",
    fontSize: "1rem",
    fontWeight: 500,
  },
};

const UpdatePasswordForm = () => {
  const [currentValue, setCurrentValue] = useState<string | undefined>(
    undefined
  );
  const [newValue, setNewValue] = useState<string | undefined>(undefined);
  const [newValueConfirmation, setNewValueConfirmation] = useState<
    string | undefined
  >(undefined);

  const { error, success } = NotificationService();
  const { updatePassword } = UserService();
  const { close } = DialogService();

  const saveData = () => {
    if (currentValue && newValue && newValueConfirmation) {
      if (newValue === newValueConfirmation) {
        updatePassword(currentValue, newValue, newValueConfirmation).then(
          (result) => {
            if (result) {
              success("Password updated");
            } else {
              error("Password update error");
            }
            close();
          }
        );
      } else {
        error("Please, confirmation error");
      }
    } else {
      error("Please, fill all the data");
    }
  };

  return (
    <Box sx={{ marginTop: "1rem" }}>
      <Box sx={{ marginBottom: "1rem" }}>
        <TextField
          label="Current Password"
          variant="outlined"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          type="password"
          fullWidth
          sx={tfMainStyle}
          InputLabelProps={tfLabelProps}
          InputProps={tfInputProps}
        />
      </Box>
      <Box sx={{ marginBottom: "1rem" }}>
        <TextField
          label="New Password"
          variant="outlined"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          type="password"
          fullWidth
          sx={tfMainStyle}
          InputLabelProps={tfLabelProps}
          InputProps={tfInputProps}
        />
      </Box>
      <Box sx={{ marginBottom: "1rem" }}>
        <TextField
          label="New Password Confirmation"
          variant="outlined"
          value={newValueConfirmation}
          onChange={(e) => setNewValueConfirmation(e.target.value)}
          type="password"
          fullWidth
          error={newValue !== newValueConfirmation}
          helperText="Should be the same value of 'New Password'"
          sx={tfMainStyle}
          InputLabelProps={tfLabelProps}
          InputProps={tfInputProps}
        />
      </Box>
      <Box>
        <Button variant="blue" onClick={saveData}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

interface UpdateValueFormProps {
  currentValue: string | undefined;
  setValue: Function;
  description?: boolean;
  password?: boolean;
}

const UpdateValueForm = ({
  description,
  password,
  currentValue,
  setValue,
}: UpdateValueFormProps) => {
  const [newValue, setNewValue] = useState<string | undefined>(currentValue);

  const { close } = DialogService();

  const saveData = () => {
    if (currentValue !== newValue) {
      setValue(newValue);
    }
    close();
  };

  return (
    <Box>
      <Box sx={{ marginY: "1rem" }}>
        <TextField
          variant="outlined"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          type={password ? "password" : "text"}
          multiline={description}
          fullWidth
          sx={tfMainStyle}
          InputLabelProps={tfLabelProps}
          InputProps={tfInputProps}
        />
      </Box>
      <Box>
        <Button variant="customBlueText" onClick={saveData}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

const MyAccount = () => {
  const loginData = useSelector((state: RootState) => state.login);

  const [newProfileImage, setNewProfileImage] = useState<Image | undefined>(
    undefined
  );
  const [newProfileImageUrl, setNewProfileImageUrl] = useState<
    string | undefined
  >(loginData.profileImage?.url);

  const [newHeaderImage, setNewHeaderImage] = useState<Image | undefined>(
    undefined
  );
  const [newHeaderImageUrl, setNewHeaderImageUrl] = useState<
    string | undefined
  >(loginData.headerImage?.url);

  const [newAboutMe, setNewAboutMe] = useState<string | undefined>(
    loginData.userData?.about_me
  );
  const [newEmail, setNewEmail] = useState<string | undefined>(loginData.email);
  const [isPrivate, setIsPrivate] = useState<boolean>(
    loginData.collection?.private ? loginData.collection.private : false
  );
  const [publicFinStats, setPublicFinStats] = useState<boolean>(
    loginData.userData?.view_financial_stats
      ? loginData.userData?.view_financial_stats
      : false
  );
  const [publicNumericStats, setPublicNumericStats] = useState<boolean>(
    loginData.userData?.view_numerical_stats
      ? loginData.userData?.view_numerical_stats
      : false
  );

  const { close: closeBDL } = BackdropLoaderService();
  const { saveUser } = LoginService();
  const { openXS } = DialogService();
  const { error, success } = NotificationService();
  const { b64toBlob } = FormatService();
  const {
    updateProfileImage,
    updateHeaderImage,
    updateEmail,
    updateAboutMe,
    updateCollectionPrivacy,
    updateCollectionFinancialStatsPrivacy,
    updateCollectionNumericStatsPrivacy,
  } = UserService();

  const openUpdateDialog = (
    title: string,
    currentValue: string | undefined,
    setValue: Function,
    password: boolean,
    description: boolean
  ) => {
    openXS(
      `${title}`,
      <UpdateValueForm
        currentValue={currentValue}
        setValue={setValue}
        password={password}
        description={description}
      />,
      []
    );
  };

  const openUpdatePasswordForm = () => {
    openXS("Update Password", <UpdatePasswordForm />, []);
  };

  const openUpdateAddressDialog = () => {
    openUpdateDialog(
      "Update " + LABELS.EMAIL_ADDRESS,
      loginData.email,
      setNewEmail,
      false,
      false
    );
  };

  const openUpdateAboutMeDialog = () => {
    openUpdateDialog(
      "Update " + LABELS.ABOUT_ME,
      newAboutMe,
      setNewAboutMe,
      false,
      true
    );
  };

  useEffect(() => {
    closeBDL();
    if (newProfileImage?.data) {
      const blob = b64toBlob(newProfileImage.data);
      const blobUrl = URL.createObjectURL(blob);
      setNewProfileImageUrl(blobUrl);
      updateProfileImage(newProfileImage).then((result) => {
        if (result) {
          saveUser({ ...loginData, profileImage: result });
          success("Profile Image Updated");
        } else {
          error("Profile Image Update error");
        }
      });
    }
  }, [newProfileImage]);

  useEffect(() => {
    closeBDL();
    if (newHeaderImage?.data) {
      const blob = b64toBlob(newHeaderImage.data);
      const blobUrl = URL.createObjectURL(blob);
      setNewHeaderImageUrl(blobUrl);
      updateHeaderImage(newHeaderImage).then((result) => {
        if (result) {
          success("Header Image updated");
          saveUser({ ...loginData, headerImage: result });
        } else {
          error("Header Image update error");
        }
      });
    }
  }, [newHeaderImage]);

  useEffect(() => {
    if (newAboutMe && newAboutMe !== loginData.userData?.about_me) {
      updateAboutMe(newAboutMe).then((result) => {
        if (result) {
          success("About Me updated");
          saveUser({
            ...loginData,
            userData: {
              ...loginData.userData,
              about_me: newAboutMe,
            } as UserData,
          });
        } else {
          error("About Me update error");
        }
      });
    }
  }, [newAboutMe]);

  useEffect(() => {
    if (newEmail && newEmail !== loginData.email) {
      updateEmail(newEmail).then((result) => {
        if (result) {
          success("Email updated");
          saveUser({ ...loginData, email: newEmail });
        } else {
          error("Email update error");
        }
      });
    }
  }, [newEmail]);

  useEffect(() => {
    if (isPrivate !== loginData.collection?.private) {
      updateCollectionPrivacy(isPrivate).then((result) => {
        if (result) {
          success("Collection privacy updated");
          if (loginData.collection) {
            const collection: Collection = {
              id: loginData.collection.id,
              private: isPrivate,
            };
            saveUser({ ...loginData, collection: collection });
          }
        } else {
          error("Collection privacy update error");
        }
      });
    }
  }, [isPrivate]);

  useEffect(() => {
    if (publicFinStats !== loginData.userData?.view_financial_stats) {
      updateCollectionFinancialStatsPrivacy(publicFinStats).then((result) => {
        if (result) {
          success("Financial stats privacy updated");
          saveUser({
            ...loginData,
            userData: {
              ...loginData.userData,
              view_financial_stats: publicFinStats,
            } as UserData,
          });
        } else {
          error("Financial stats privacy update error");
        }
      });
    }
  }, [publicFinStats]);

  useEffect(() => {
    if (publicNumericStats !== loginData.userData?.view_numerical_stats) {
      updateCollectionNumericStatsPrivacy(publicNumericStats).then((result) => {
        if (result) {
          success("Numeric Stats privacy updated");
          saveUser({
            ...loginData,
            userData: {
              ...loginData.userData,
              view_numerical_stats: publicNumericStats,
            } as UserData,
          });
        } else {
          error("Numeric Stats privacy update error");
        }
      });
    }
  }, [publicNumericStats]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        padding: { xs: "2.5rem 1rem", sm: "2.5rem 2rem" },
        borderRadius: "10px",
        backgroundColor: "white !important",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="followCardUserName"
          sx={{ fontSize: "1.5rem !important" }}
        >
          {LABELS.TITLE}
        </Typography>
      </Box>

      <Box
        sx={{
          width: { xs: "100%", sm: "calc(50% - 8px)" },
          marginTop: "1rem",
        }}
      >
        <TextFieldUpdater
          label={LABELS.USER_NAME}
          value={loginData.userName}
          disabled
        />
      </Box>

      <Box
        sx={{
          width: { xs: "100%", sm: "calc(50% - 8px)" },
          marginTop: "1rem",
        }}
      >
        <TextFieldUpdater
          label={LABELS.EMAIL_ADDRESS}
          value={loginData.email}
          onEdit={openUpdateAddressDialog}
        />
      </Box>

      <Box
        sx={{
          width: { xs: "100%", sm: "calc(50% - 8px)" },
          marginTop: "1rem",
        }}
      >
        <TextFieldUpdater
          label={LABELS.PASSWORD}
          value="**********"
          onEdit={openUpdatePasswordForm}
        />
      </Box>

      <Box sx={{ width: "100%", marginTop: "2rem" }}>
        <Typography
          variant="shirtViewText"
          sx={{
            marginBottom: "0.5rem",
            color: "#6f7d95 !important",
            fontWeight: "600 !important",
          }}
        >
          {LABELS.PROFILE_PICTURE}
        </Typography>
        <ProfileImgForm
          size={60}
          imgUrl={newProfileImageUrl}
          setValue={setNewProfileImage}
        />
      </Box>
      <Box sx={{ width: "100%", marginTop: "2rem" }}>
        <Typography
          variant="shirtViewText"
          sx={{
            marginBottom: "0.5rem",
            color: "#6f7d95 !important",
            fontWeight: "600 !important",
          }}
        >
          {LABELS.HEADER_PICTURE}
        </Typography>
        <HeaderImgForm
          imageUrl={newHeaderImageUrl}
          setValue={setNewHeaderImage}
        />
      </Box>
      <Box sx={{ marginY: "2rem" }}>
        <Box
          sx={{
            marginBottom: "10px",
          }}
        >
          <Typography
            variant="shirtViewText"
            sx={{
              marginBottom: "0.5rem",
              color: "#6f7d95 !important",
              fontWeight: "600 !important",
            }}
          >
            {LABELS.ABOUT_ME}
          </Typography>

          <Typography variant="shirtViewText">{newAboutMe}</Typography>
        </Box>
        <Box>
          <Button
            variant="customBlueTextUnderLine"
            sx={{
              textDecorationThickness: "2px",
              textUnderlineOffset: "5px",
              fontWeight: "500 !important",
            }}
            onClick={openUpdateAboutMeDialog}
          >
            {LABELS.BTN_ABOUT_ME}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          rowGap: 2,
        }}
      >
        <Box>
          <Typography
            variant="shirtViewText"
            sx={{
              marginBottom: "0.5rem",
              color: "#6f7d95 !important",
              fontWeight: "600 !important",
            }}
          >
            {LABELS.VISIBILITY_COLLECTION}
          </Typography>
          <Box sx={{ width: 250 }}>
            <PremiumWrapperButton
              action={Actions.UPDATE_VISIBILITY}
              entity={Subjects.COLLECTION}
              component={
                <CustomSwitch
                  checked={!isPrivate}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setIsPrivate(!val);
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
            />
          </Box>
        </Box>
        {/* <Box>
          <Typography
            variant="shirtViewText"
            sx={{
              marginBottom: "0.5rem",
              color: "#6f7d95 !important",
              fontWeight: "600 !important",
            }}
          >
            {LABELS.VISIBILITY_FIN_STATS}
          </Typography>
          <Box sx={{ width: 250 }}>
            <PremiumWrapperButton
              action={Actions.UPDATE_FINANCIAL_VISIBILITY}
              entity={Subjects.STATS}
              component={
                <CustomSwitch
                  checked={publicFinStats}
                  onChange={(e) => setPublicFinStats(e.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
            />
          </Box>
        </Box> */}
        <Box>
          <Typography
            variant="shirtViewText"
            sx={{
              marginBottom: "0.5rem",
              color: "#6f7d95 !important",
              fontWeight: "600 !important",
            }}
          >
            {LABELS.VISIBILITY_NUM_STATS}
          </Typography>
          <Box sx={{ width: 250 }}>
            <PremiumWrapperButton
              action={Actions.UPDATE_NUMERIC_VISIBILITY}
              entity={Subjects.STATS}
              component={
                <CustomSwitch
                  checked={publicNumericStats}
                  onChange={(e) => setPublicNumericStats(e.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MyAccount;
